import { Button, Divider, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { ComponentType } from "react";
import { CoffeeSolidStandardIcon, Icon } from "@/components/common/icons";
import { WorkShiftsTrackerAction } from "@/components/serviceFlow/clockin/workShiftsTracker/workShiftsTracker";
import {
  GREY,
  PRIMARY,
  TEXT_PRIMARY_DARK,
  TEXT_SECONDARY,
  WARNING,
  WARNING_PRIMARY,
} from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { ShiftStatus } from "@/types";

type WorkShiftsTrackerPopupProps = {
  actions: WorkShiftsTrackerAction[];
  shiftStartDate: Date;
  shiftDurationSeconds: number | undefined;
  breaksCumulativeDurationMinutes: number;
  shiftStatus: ShiftStatus;
};

const MOBILE_ADDITIONAL_TEXT: Partial<Record<ShiftStatus, string>> = {
  [ShiftStatus.CLOCKED_IN]: "You are on the clock",
  [ShiftStatus.ON_BREAK]: "You are on a break",
};

const MOBILE_ADDITIONAL_ICON: Partial<
  Record<ShiftStatus, ComponentType<Icon> | undefined>
> = {
  [ShiftStatus.CLOCKED_IN]: undefined,
  [ShiftStatus.ON_BREAK]: CoffeeSolidStandardIcon,
};

const MOBILE_ADDITIONAL_DURATION_COLOR: Partial<Record<ShiftStatus, string>> = {
  [ShiftStatus.CLOCKED_IN]: PRIMARY,
  [ShiftStatus.ON_BREAK]: WARNING[80],
};

export default function WorkShiftsTrackerPopup({
  actions,
  shiftStartDate,
  shiftDurationSeconds,
  breaksCumulativeDurationMinutes,
  shiftStatus,
}: WorkShiftsTrackerPopupProps) {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const Icon = MOBILE_ADDITIONAL_ICON[shiftStatus];

  const totalSeconds = Math.floor((shiftDurationSeconds ?? 0) % 60);
  const totalMinutes = Math.floor(((shiftDurationSeconds ?? 0) / 60) % 60);
  const totalHours = Math.floor((shiftDurationSeconds ?? 0) / 60 / 60);

  return (
    <Stack
      sx={{
        alignItems: "flex-start",
        ...(isMobile && {
          width: "calc(100vw - 32px)",
        }),
      }}
    >
      <Stack
        sx={{
          width: "100%",
          gap: 1,
          ...(isMobile && {
            p: 1.5,
            pb: 1,
          }),
        }}
      >
        {isMobile && (
          <Stack
            direction="row"
            sx={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h3">
              {MOBILE_ADDITIONAL_TEXT[shiftStatus]}
            </Typography>
            <Stack direction="row" sx={{ gap: 0.5 }}>
              {Icon && <Icon size={20} color={WARNING_PRIMARY} />}
              {shiftDurationSeconds !== undefined && (
                <Typography
                  variant="h2"
                  color={MOBILE_ADDITIONAL_DURATION_COLOR[shiftStatus]}
                >
                  {totalHours}:{totalMinutes.toString().padStart(2, "0")}:
                  {totalSeconds.toString().padStart(2, "0")}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
        {actions.map((action) => (
          <Button
            key={action.label}
            onClick={action.onClick}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 0,
              width: "100%",
              justifyContent: "flex-start",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {action.icon}
              <Typography variant="paragraphSmall" color={TEXT_PRIMARY_DARK}>
                {action.label}
              </Typography>
            </Stack>
          </Button>
        ))}
      </Stack>
      {actions.length > 0 && <Divider sx={{ color: GREY[20] }} flexItem />}
      <Stack
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: GREY[5],
          width: "100%",
        }}
      >
        <Typography
          variant={isMobile ? "paragraphSmall" : "paragraph"}
          color={TEXT_SECONDARY}
        >
          Shift started at {format(shiftStartDate, "p")}
        </Typography>
        <Typography
          variant={isMobile ? "paragraphTiny" : "paragraphSmall"}
          color={TEXT_SECONDARY}
        >
          {breaksCumulativeDurationMinutes} min(s) of break
        </Typography>
      </Stack>
    </Stack>
  );
}
